import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { FaRupeeSign } from 'react-icons/fa';

const { FiActivity, FiTrendingUp, FiTrendingDown, FiRefreshCw, FiTarget } = FiIcons;

const StrikePriceCalculator = () => {
  const [price, setPrice] = useState('');
  const [direction, setDirection] = useState(null); // 'CALL' or 'PUT'

  const calculateStrike = (currentPrice, type) => {
    if (!currentPrice || isNaN(currentPrice) || !type) return null;
    
    const val = Math.floor(parseFloat(currentPrice));
    const lastTwo = val % 100;
    const base = Math.floor(val / 100) * 100;

    let resultStrike;

    if (lastTwo >= 0 && lastTwo <= 15) {
      // 00 - 15 Range
      if (type === 'CALL') {
        resultStrike = base - 100;
      } else {
        resultStrike = base + 100;
      }
    } else if (lastTwo >= 85 && lastTwo <= 99) {
      // 85 - 99 Range
      if (type === 'CALL') {
        resultStrike = base; // Round down to 00
      } else {
        resultStrike = base + 200; // Corrected: Round down to 00 + 200 (e.g., 25090 -> 25200)
      }
    } else {
      // 16 - 84 Range
      if (type === 'CALL') {
        resultStrike = base;
      } else {
        resultStrike = base + 100;
      }
    }

    return resultStrike;
  };

  const optimumStrike = calculateStrike(price, direction);

  return (
    <div className="w-full max-w-xl mx-auto px-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-black/60 backdrop-blur-xl border border-white/10 rounded-[2rem] overflow-hidden shadow-[0_0_50px_rgba(0,0,0,0.5)]"
      >
        {/* Header */}
        <div className="p-6 md:p-8 border-b border-white/10 bg-gradient-to-r from-red-900/30 to-transparent relative overflow-hidden">
          <div className="absolute top-0 right-0 p-8 opacity-10">
            <SafeIcon icon={FiTarget} className="w-32 h-32 text-red-500" />
          </div>
          <h2 className="text-2xl md:text-3xl font-black text-white uppercase tracking-tighter mb-2 relative z-10 drop-shadow-lg">
            Strike <span className="text-red-500 glow-text-red">Finder</span>
          </h2>
          <p className="text-white/60 text-sm font-medium tracking-wide relative z-10">
            Get the optimum contract strike price instantly.
          </p>
        </div>

        <div className="p-6 md:p-8 space-y-8">
          {/* Input Section */}
          <div className="space-y-6">
            <div className="relative group">
              <label className="block text-xs font-bold text-red-500 uppercase tracking-widest mb-2 ml-1 glow-text-red">
                Current Market Price
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <SafeIcon icon={FaRupeeSign} className="text-white/40 w-4 h-4 group-focus-within:text-red-500 transition-colors" />
                </div>
                <input
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="0.00"
                  className="w-full bg-black/40 border border-white/10 rounded-xl py-4 pl-12 pr-12 text-2xl md:text-3xl font-black text-white placeholder-white/20 focus:outline-none focus:border-red-500/50 focus:ring-1 focus:ring-red-500/20 focus:shadow-[0_0_20px_rgba(220,38,38,0.2)] transition-all"
                />
                 {price && (
                  <button 
                    onClick={() => {
                      setPrice('');
                      setDirection(null);
                    }}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-white/40 hover:text-white transition-colors hover:scale-110"
                  >
                    <SafeIcon icon={FiRefreshCw} className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>

            {/* Direction Selection */}
            <div className="space-y-2">
              <label className="block text-xs font-bold text-white/50 uppercase tracking-widest ml-1">
                Are you taking a Call or Put?
              </label>
              <div className="grid grid-cols-2 gap-4">
                <motion.button
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setDirection('CALL')}
                  className={`relative overflow-hidden p-4 rounded-xl border flex flex-col items-center gap-2 transition-all duration-300 ${
                    direction === 'CALL' 
                      ? 'bg-green-500/20 border-green-500 text-green-500 shadow-[0_0_25px_rgba(34,197,94,0.3)]' 
                      : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10 hover:border-green-500/30 hover:text-green-500'
                  }`}
                >
                  <SafeIcon icon={FiTrendingUp} className="w-6 h-6" />
                  <span className="font-black uppercase tracking-widest text-sm">CALL (CE)</span>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setDirection('PUT')}
                  className={`relative overflow-hidden p-4 rounded-xl border flex flex-col items-center gap-2 transition-all duration-300 ${
                    direction === 'PUT' 
                      ? 'bg-red-500/20 border-red-500 text-red-500 shadow-[0_0_25px_rgba(239,68,68,0.3)]' 
                      : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10 hover:border-red-500/30 hover:text-red-500'
                  }`}
                >
                  <SafeIcon icon={FiTrendingDown} className="w-6 h-6" />
                  <span className="font-black uppercase tracking-widest text-sm">PUT (PE)</span>
                </motion.button>
              </div>
            </div>
          </div>

          {/* Result Section */}
          <AnimatePresence mode="wait">
            {optimumStrike !== null && (
              <motion.div
                key="result"
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                className="pt-4"
              >
                <div className={`relative overflow-hidden rounded-2xl p-8 text-center border backdrop-blur-sm ${
                  direction === 'CALL' 
                    ? 'bg-green-900/20 border-green-500/30 shadow-[0_0_30px_rgba(22,163,74,0.15)]' 
                    : 'bg-red-900/20 border-red-500/30 shadow-[0_0_30px_rgba(220,38,38,0.15)]'
                }`}>
                  <div className={`absolute top-0 left-0 w-full h-1 ${
                    direction === 'CALL' ? 'bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.8)]' : 'bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.8)]'
                  }`}></div>
                  
                  <h3 className="text-white/60 text-xs font-bold uppercase tracking-[0.2em] mb-4">
                    Optimum Strike Price
                  </h3>
                  
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <span className={`text-5xl md:text-6xl font-black tracking-tighter drop-shadow-2xl ${
                      direction === 'CALL' ? 'text-white glow-text-green' : 'text-white glow-text-red'
                    }`}>
                      {optimumStrike}
                    </span>
                    <span className={`text-xl font-black uppercase self-end mb-2 ${
                      direction === 'CALL' ? 'text-green-500' : 'text-red-500'
                    }`}>
                      {direction === 'CALL' ? 'CE' : 'PE'}
                    </span>
                  </div>

                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${
                    direction === 'CALL' 
                      ? 'bg-green-500/10 border-green-500/20 text-green-500' 
                      : 'bg-red-500/10 border-red-500/20 text-red-500'
                  }`}>
                    <SafeIcon icon={FiActivity} className="w-3 h-3" />
                    Recommended Contract
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default StrikePriceCalculator;